/**
 * Authentication Context for managing user authentication state
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, UserLogin, UserCreate, UserUpdate, Token } from '../types';
import { login as apiLogin, register as apiRegister, getCurrentUser, updateProfile as apiUpdateProfile, logout as apiLogout } from '../services/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (loginData: UserLogin) => Promise<void>;
  register: (userData: UserCreate) => Promise<void>;
  updateProfile: (userData: UserUpdate) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load auth state from localStorage on mount
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Error parsing stored user:', e);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
      }
    }

    // Verify token by fetching current user
    if (storedToken) {
      getCurrentUser()
        .then((currentUser) => {
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        })
        .catch(() => {
          // Token invalid, clear auth
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (loginData: UserLogin) => {
    const tokenData = await apiLogin(loginData);
    console.log('Login successful, token received:', tokenData.access_token.substring(0, 20) + '...');
    setToken(tokenData.access_token);
    localStorage.setItem('auth_token', tokenData.access_token);

    // Fetch user info
    try {
      const currentUser = await getCurrentUser();
      console.log('Current user fetched:', currentUser);
      setUser(currentUser);
      localStorage.setItem('user', JSON.stringify(currentUser));
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      throw error;
    }
  };

  const register = async (userData: UserCreate) => {
    await apiRegister(userData);
    // After registration, automatically login
    await login({ username: userData.username, password: userData.password });
  };

  const updateProfile = async (userData: UserUpdate) => {
    const updatedUser = await apiUpdateProfile(userData);
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const logout = () => {
    apiLogout();
    setToken(null);
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    login,
    register,
    updateProfile,
    logout,
    isAuthenticated: !!token && !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

